import { useEffect, useRef } from 'react'
import { useMatch } from 'react-router-dom'
import { AnalyticsEvent } from '@/analytics/schema/events.schema'
import { useAnalytics } from '@/analytics/hooks/useAnalytics'
import { store } from '@/store/store'

/**
 * Listens for global copy / paste events and pushes analytics context.
 * Must be rendered **inside** RouterProvider so that useMatch has router context.
 */
export default function ClipboardAnalyticsListener() {
  const { trackEvent } = useAnalytics()
  const lastFeatureRef = useRef<string | null>(null)

  // Matches update automatically on navigation
  const chatMatch = useMatch('/dashboard/m/:matterId/chat/:feature/*')
  const conversationMatch = useMatch('/dashboard/m/:matterId/chat/:feature/:conversationId/*')

  useEffect(() => {
    const handleEvent = (eventType: AnalyticsEvent) => {
      const feature = chatMatch?.params.feature ?? ''
      const conversationId = conversationMatch?.params.conversationId ?? ''

      const state = store.getState()
      const skillData = conversationId ? state.skillsState.activeSkillForConversation[conversationId] : undefined

      const properties: Record<string, string> = {}

      if (skillData?.skill) {
        properties.skill = skillData.skill
        properties.skillSource = skillData.skillSource ?? ''
      } else if (feature !== lastFeatureRef.current) {
        // reset when switching feature without skill
        properties.skill = ''
        properties.skillSource = ''
      }

      lastFeatureRef.current = feature
      trackEvent(eventType, properties)
    }

    const onCopy = () => handleEvent(AnalyticsEvent.ClipboardCopy)
    const onPaste = () => handleEvent(AnalyticsEvent.ClipboardPaste)

    document.addEventListener('copy', onCopy)
    document.addEventListener('paste', onPaste)
    return () => {
      document.removeEventListener('copy', onCopy)
      document.removeEventListener('paste', onPaste)
    }
  }, [trackEvent, chatMatch, conversationMatch])

  return null
}
